import {ActionIcon, Group, Modal} from '@mantine/core';
import {IconX} from '@tabler/icons-react';
import {ContentTitle} from 'src/design/atoms/Typography';

/**
 * Full-screen modal (header bar + body); `fillHeight` makes the body a
 * full-height flex column so a child can own its own scroll region.
 * @registry Full-screen Mantine Modal for QR codes and other immersive content.
 */

const OVERLAY_Z_INDEX = 1001;

export interface FullScreenModalProps {
  title?: string;
  open: boolean;
  onCloseCallback: () => void;
  /** Body becomes a full-height flex column so a child can fill/scroll it. */
  fillHeight?: boolean;
  /** Locks the modal: header close icon disabled, escape/outside-click ignored. */
  closeDisabled?: boolean;
  /** Pinned below the body; never scrolls with it. */
  footer?: React.ReactNode;
  /** Right-aligned header content (actions menu) beside the title. */
  headerRightSection?: React.ReactNode;
  children?: React.ReactNode;
}

export const FullScreenModal = ({
  title,
  open,
  onCloseCallback,
  fillHeight = false,
  closeDisabled = false,
  footer,
  headerRightSection,
  children,
}: FullScreenModalProps) => {
  return (
    <Modal
      fullScreen
      opened={open}
      onClose={onCloseCallback}
      withCloseButton={false}
      closeOnEscape={!closeDisabled}
      closeOnClickOutside={!closeDisabled}
      transitionProps={{transition: 'slide-up'}}
      zIndex={OVERLAY_Z_INDEX}
      padding={0}
      styles={
        fillHeight
          ? {
              content: {display: 'flex', flexDirection: 'column'},
              body: {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              },
            }
          : undefined
      }>
      <Group p="md" gap="sm" wrap="nowrap" style={{flexShrink: 0}}>
        <ActionIcon
          variant="subtle"
          color="black"
          aria-label="close"
          disabled={closeDisabled}
          onClick={onCloseCallback}>
          <IconX />
        </ActionIcon>
        <ContentTitle text={title} />
        {headerRightSection && (
          <Group gap="xs" wrap="nowrap" ml="auto" style={{flexShrink: 0}}>
            {headerRightSection}
          </Group>
        )}
      </Group>
      <div
        style={{
          margin: 'var(--mantine-spacing-md)',
          ...(fillHeight
            ? {flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}
            : {}),
        }}>
        {children}
      </div>
      {footer && (
        <div
          style={{
            margin: 'var(--mantine-spacing-md)',
            marginTop: 0,
            flexShrink: 0,
          }}>
          {footer}
        </div>
      )}
    </Modal>
  );
};
